'use client';

import { type FC, type ReactNode } from 'react';
import type { Team } from '@cs-shot/battle-sim';
import { ScreenContent, ScreenFixedTop, ScreenFixedBottom } from '../../../screen';
import { Button, Avatar } from '../../ui';
import { Credits } from '../../Credits';
import { FaTrophy } from '../../icons';
import { getLevelIcon, getLevelGradientClass } from '../../LevelBadge';
import { formatCreditsNumber } from '../../../utils/formatters';
import { cn } from '../../../utils/cn';
import { useLocalTranslation } from '../../../hooks/useLocalTranslation';
import translations from './RushMid.translations';
import { modeLabel } from './HistoryScreen';
import { sideColor } from './sideColor';
import { SideIcon } from '../SideIcon';
import { rushMidAvatarUrl } from './avatarUrl';
import { RushMidSearchTip } from './searchTips';
import { useSecondsUntil } from './openGameCountdown';
import { bustCache } from '../render/battleAssetVersion';
import { useStoredImageSrc } from '../../../lib/useStoredAsset';

type Translate = ReturnType<typeof useLocalTranslation>['t'];

export interface OpenSeat {
  userId: string;
  name: string;
  level: number;
  rating?: number;
  partyGroup?: number;
  bot?: boolean;
}

export interface OpenGameView {
  matchId: string;
  ticket: number;
  mode: number;
  map: string;
  seats: { t: readonly (OpenSeat | null)[]; ct: readonly (OpenSeat | null)[] };
  botFillAt?: string;
  yourSide?: Team;
}

interface OpenGameScreenProps {
  game: OpenGameView;
  youUserId?: string;
  onJoin?: (side: Team) => void;
  shortfall?: number;
  onLeave?: () => void;
  leaveVariant?: 'game' | 'party';
  onBack?: () => void;
  onInvite?: () => void;
  inviting?: boolean;
  joining?: boolean;
  partyK?: number;
  partyBlocked?: boolean;
  joinBlockedUntil?: string;
}

export type Segment =
  | { kind: 'party'; group: number; seats: OpenSeat[] }
  | { kind: 'solo'; seat: OpenSeat }
  | { kind: 'free'; index: number };

export function segments(seats: readonly (OpenSeat | null)[]): Segment[] {
  const out: Segment[] = [];
  let i = 0;
  while (i < seats.length) {
    const s = seats[i];
    if (s && s.partyGroup !== undefined) {
      const group = s.partyGroup;
      const run: OpenSeat[] = [];
      while (i < seats.length) {
        const cur = seats[i];
        if (cur && cur.partyGroup === group) { run.push(cur); i += 1; } else break;
      }
      out.push({ kind: 'party', group, seats: run });
    } else if (s) {
      out.push({ kind: 'solo', seat: s });
      i += 1;
    } else {
      out.push({ kind: 'free', index: i });
      i += 1;
    }
  }
  return out;
}

const freeSlots = (seats: readonly (OpenSeat | null)[]): number => seats.filter((s) => s === null).length;

const RatingLabel: FC<{ rating?: number }> = ({ rating }) => (
  <div className="ml-auto inline-flex shrink-0 items-center gap-1 text-xs font-semibold tabular-nums text-label-secondary">
    <FaTrophy size={10} className="text-side-t" />
    {rating === undefined ? '—' : formatCreditsNumber(rating)}
  </div>
);

const SeatAvatar: FC<{ seat: OpenSeat; you: boolean }> = ({ seat, you }) => {
  const LevelIcon = getLevelIcon(seat.level);
  return (
    <div className="relative shrink-0">
      <Avatar
        size="s"
        className={cn(you && 'ring-2 ring-white')}
        src={rushMidAvatarUrl(seat.userId)}
        fallbackKey={seat.userId}
        firstName={seat.name}
      />
      <div className={cn(
        'absolute -bottom-1 left-1/2 flex -translate-x-1/2 items-center gap-px rounded-full px-1 py-0.5 text-white shadow-[0_2px_6px_rgba(0,0,0,0.3)]',
        getLevelGradientClass(seat.level),
      )}
      >
        <LevelIcon className="text-[8px]" />
        <span className="text-[8px] font-bold leading-none">{seat.level}</span>
      </div>
    </div>
  );
};

const SeatRow: FC<{ seat: OpenSeat; you: boolean; youLabel: string }> = ({ seat, you, youLabel }) => (
  <div className="flex items-center gap-3 rounded-xl bg-black/30 px-3 py-2.5">
    <SeatAvatar seat={seat} you={you} />
    <span className={cn('min-w-0 truncate text-sm', you ? 'font-semibold text-label-primary' : 'font-medium text-label-primary')}>
      {you ? youLabel : seat.name}
    </span>
    <RatingLabel rating={seat.rating} />
  </div>
);

const FreeRow: FC<{ side: Team; onJoin?: (side: Team) => void; joining?: boolean; joinLabel: string; waitLabel: string; fits: boolean }> = ({
  side, onJoin, joining, joinLabel, waitLabel, fits,
}) =>
  onJoin ? (
    <button
      type="button"
      disabled={joining}
      onClick={() => onJoin(side)}
      className={cn(
        'flex items-center gap-3 rounded-xl border-2 border-solid px-3 py-2.5 text-start active:opacity-60 disabled:opacity-40',
        fits ? `${sideColor(side, 'border')} ${sideColor(side, 'bg')}` : 'border-white/15',
      )}
    >
      <div className={cn(
        'flex size-8 shrink-0 items-center justify-center rounded-full border-2 border-solid',
        fits ? sideColor(side, 'border') : 'border-white/30',
        fits ? sideColor(side, 'text') : 'text-white/50',
      )}
      >
        +
      </div>
      <span className={cn('min-w-0 truncate text-sm', fits ? sideColor(side, 'text') : 'text-label-secondary')}>{joinLabel}</span>
    </button>
  ) : (
    <div className={cn('flex items-center gap-3 rounded-xl border-2 border-solid px-3 py-2.5', sideColor(side, 'border'))}>
      <div className={cn(
        'flex size-8 shrink-0 animate-pulse items-center justify-center rounded-full border-2 border-solid text-base font-bold',
        sideColor(side, 'border'), sideColor(side, 'text'),
      )}
      >
        +
      </div>
      <span className={cn('text-sm font-medium opacity-80', sideColor(side, 'text'))}>{waitLabel}</span>
    </div>
  );

interface SectionProps {
  side: Team;
  seats: readonly (OpenSeat | null)[];
  youUserId?: string;
  onJoin?: (side: Team) => void;
  joining?: boolean;
  partyK?: number;
  partyBlocked?: boolean;
  isYourSide: boolean;
  t: Translate;
}

const Section: FC<SectionProps> = ({ side, seats, youUserId, onJoin, joining, partyK, partyBlocked, isYourSide, t }) => {
  const showFit = !!onJoin && partyK !== undefined && partyK >= 2;
  const fits = showFit && freeSlots(seats) >= partyK;
  const youLabel = t('youLabel');
  const joinLabel = t('joinSide');
  const waitLabel = t('waitingForPlayer');
  const joinHandler = partyBlocked ? undefined : onJoin;

  return (
    <div className={cn(
      'flex flex-col gap-2 rounded-2xl bg-gradient-to-br to-transparent p-3 ring-1',
      side === 't' ? 'from-amber-500/20 ring-side-t/30' : 'from-sky-500/20 ring-sky-500/30',
    )}
    >
      <div className="flex items-center gap-2">
        <div className={cn('flex items-center gap-2', isYourSide && `rounded-full px-2 ${sideColor(side, 'bg')}`)}>
          <SideIcon side={side} size={24} className="shrink-0" />
          <span className={`text-base font-black ${sideColor(side, 'text')}`}>{t(side === 't' ? 'sideT' : 'sideCt')}</span>
        </div>
        {showFit && (
          <span className={cn(
            'ml-auto rounded-full px-2 py-0.5 text-[10px] font-bold uppercase tracking-wide',
            fits ? `${sideColor(side, 'bg')} ${sideColor(side, 'text')}` : 'bg-white/10 text-label-secondary',
          )}
          >
            {fits ? t('partyFits', { k: partyK }) : t('wontFit')}
          </span>
        )}
      </div>
      {segments(seats).map((seg, idx) => {
        if (seg.kind === 'party') {
          return (
            <div key={`p${idx}`} className={`flex flex-col gap-1.5 rounded-xl border p-2 ${sideColor(side, 'border')}`}>
              <span className="text-[10px] font-semibold uppercase tracking-wide text-white/70">{t('partyBadge', { k: seg.seats.length })}</span>
              {seg.seats.map((seat) => (
                <SeatRow key={seat.userId} seat={seat} you={seat.userId === youUserId} youLabel={youLabel} />
              ))}
            </div>
          );
        }
        if (seg.kind === 'solo') {
          return <SeatRow key={seg.seat.userId} seat={seg.seat} you={seg.seat.userId === youUserId} youLabel={youLabel} />;
        }
        return <FreeRow key={`f${seg.index}`} side={side} onJoin={joinHandler} joining={joining} joinLabel={joinLabel} waitLabel={waitLabel} fits={fits} />;
      })}
    </div>
  );
};

export const OpenGameScreen: FC<OpenGameScreenProps> = ({
  game, youUserId, onJoin, shortfall = 0, onLeave, leaveVariant = 'game', onBack, onInvite, inviting, joining, partyK, partyBlocked,
  joinBlockedUntil,
}) => {
  const { t } = useLocalTranslation(translations);
  const seconds = useSecondsUntil(game.botFillAt);
  const graceLeft = useSecondsUntil(joinBlockedUntil);
  const graceLocked = !!joinBlockedUntil && (graceLeft === null || graceLeft > 0);
  const cover = useStoredImageSrc(bustCache(`/battle/cover_${game.map}.webp`));
  const showMemberBadge = !!onJoin && partyK !== undefined && partyK >= 2;
  const bothFull = game.seats.t.every((s) => s !== null) && game.seats.ct.every((s) => s !== null);

  const header: ReactNode = (
    <div className="relative overflow-hidden rounded-2xl bg-bg-secondary">
      {cover && <img src={cover} alt="" aria-hidden className="absolute inset-0 h-full w-full object-cover" />}
      <div className="absolute inset-0 bg-gradient-to-r from-black/55 via-black/35 to-black/55" />
      <div className="relative flex flex-col items-center gap-2 px-4 py-5">
        <span className="rounded-full bg-white/10 px-2.5 py-1 text-[11px] font-bold uppercase tracking-widest text-white shadow-[inset_0_1px_0_rgba(255,255,255,0.22)] backdrop-blur-md">
          {game.map}
        </span>
        <div className="flex items-center gap-2 text-2xl font-black text-white">
          <Credits amount={game.ticket} iconSize={20} /> <span className="text-white/60">·</span> {modeLabel(game.mode, t)}
        </div>
        <span className="inline-flex items-center gap-1 rounded-full bg-white/10 px-2.5 py-1 text-xs font-semibold text-white shadow-[inset_0_1px_0_rgba(255,255,255,0.22)] backdrop-blur-md">
          {t('winRangeChip')} <Credits amount={Math.round(game.ticket * 1.5)} iconSize={12} />
          <span className="text-white/60">—</span>
          <Credits amount={game.ticket * 5} iconSize={12} />
        </span>
        {shortfall > 0 && (
          <span className="inline-flex items-center gap-1 rounded-full bg-side-t/15 px-2.5 py-1 text-xs font-semibold text-side-t">
            {t('notEnoughShort')} <Credits amount={shortfall} iconSize={12} /> · {t('topUpBalance')}
          </span>
        )}
        {seconds !== null && (
          <div data-testid="rushmid-bot-fill" className="rounded-full bg-side-t/15 px-3 py-1 text-xs font-semibold text-side-t">
            {t('botFillIn', { s: seconds })}
          </div>
        )}
        {graceLocked && graceLeft !== null && (
          <div className="rounded-full bg-white/10 px-3 py-1 text-center text-xs font-semibold text-white/80">
            {t('graceLockedHint', { s: graceLeft })}
          </div>
        )}
        {showMemberBadge && (
          <div className="rounded-full bg-black/50 px-3 py-1 text-xs font-semibold text-white/70">
            {t('memberBadge', { k: partyK })}
          </div>
        )}
      </div>
    </div>
  );

  return (
    <>
      <ScreenFixedTop title={t('openGameTitle')} />
      <ScreenContent hideLegalSection scrollKey="rush-mid-open-game">
        <div className="flex flex-col gap-4 px-4 pt-2">
          {header}
          <div className="relative flex flex-col gap-6">
            <Section
              side="t"
              seats={game.seats.t}
              youUserId={youUserId}
              onJoin={onJoin}
              joining={joining}
              partyK={partyK}
              partyBlocked={partyBlocked || graceLocked}
              isYourSide={game.yourSide === 't'}
              t={t}
            />
            <div className="pointer-events-none absolute left-1/2 top-1/2 z-10 flex size-12 -translate-x-1/2 -translate-y-1/2 items-center justify-center rounded-full bg-black text-base font-black italic text-white shadow-[0_0_24px_rgba(0,0,0,0.8)] ring-2 ring-white/20">
              VS
            </div>
            <Section
              side="ct"
              seats={game.seats.ct}
              youUserId={youUserId}
              onJoin={onJoin}
              joining={joining}
              partyK={partyK}
              partyBlocked={partyBlocked || graceLocked}
              isYourSide={game.yourSide === 'ct'}
              t={t}
            />
          </div>
          {onJoin && bothFull && <div className="text-center text-xs text-label-secondary">{t('gameFull')}</div>}
          {onLeave && (
            <div className="pt-2">
              <RushMidSearchTip />
            </div>
          )}
        </div>
      </ScreenContent>
      <ScreenFixedBottom className="flex flex-col gap-2 px-4">
        {onInvite && (
          <Button variant="secondary" size="l" stretched onClick={onInvite} disabled={inviting}>
            {t('gameInvite')}
          </Button>
        )}
        {onBack && (
          <Button variant="tertiary" size="l" stretched onClick={onBack}>
            {t('gamesBack')}
          </Button>
        )}
        {onLeave && (
          <Button variant="tertiary" size="l" stretched onClick={onLeave}>
            {t(leaveVariant === 'party' ? 'partyLeave' : 'leaveGame')}
          </Button>
        )}
      </ScreenFixedBottom>
    </>
  );
};
